import React, { useMemo } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from '~/timeInit';

import { Alert, Autocomplete, Stack, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import type { CppPartner } from '~/types/costPerPoint';

import { type InvoiceMonth, MONTH_FORMAT, currentMonth, toMonth } from './monthRange';
import { ALL_PARTNERS_ID, isAllPartners } from './partnerSelection';

interface PartnerChoice {
  id: string;
  label: string;
}

const ALL_PARTNERS_CHOICE: PartnerChoice = { id: ALL_PARTNERS_ID, label: 'All partners' };

interface Props {
  partners: Array<CppPartner>;
  partnerId: string;
  month: InvoiceMonth;
  onPartnerChange: (partnerId: string) => void;
  onMonthChange: (month: InvoiceMonth) => void;
  /** True when the partner directory could not be loaded. */
  hasPartnersError?: boolean;
  /** Adds an All partners choice and lets the field be searched, rather than picking one id. */
  allowAllPartners?: boolean;
  /** Right-aligned actions, e.g. an export or print button. */
  actions?: React.ReactNode;
}

/**
 * Partner and month selector shared by the partner Transactions and Invoice tabs.
 *
 * Both views bill and report by whole calendar month, so the period is chosen as a month rather
 * than a free date range — there is no way to ask for a partial period that could not be invoiced.
 * Future months are unselectable; the current, still-running month is allowed, and the invoice
 * surfaces the service's `PERIOD_NOT_SETTLED` blocker for it rather than hiding it here.
 */
export default function PartnerMonthFilters({
  partners,
  partnerId,
  month,
  onPartnerChange,
  onMonthChange,
  hasPartnersError = false,
  allowAllPartners = false,
  actions,
}: Props) {
  const includeAllPartners = allowAllPartners || isAllPartners(partnerId);
  const choices = useMemo(() => {
    const partnerChoices = partners.map((partner) => ({ id: partner.id, label: partner.name }));
    return includeAllPartners ? [ALL_PARTNERS_CHOICE, ...partnerChoices] : partnerChoices;
  }, [includeAllPartners, partners]);
  const selected = choices.find((choice) => choice.id === partnerId) ?? null;

  const handleMonthChange = (next: Dayjs | null) => {
    if (next?.isValid()) {
      onMonthChange(toMonth(next));
    }
  };

  return (
    <Stack spacing={2}>
      {hasPartnersError && <Alert severity="error">Could not load the partner list. Try reloading the page.</Alert>}

      {/*
        Every control is `size="small"` and carries no helper text, so the row is a single
        consistent height and centring lines the inputs up. A conditional helper text under one
        field would make it taller than its neighbours and shift the row as soon as it cleared —
        the "select a partner" prompt lives at page level instead, where it is already shown.
      */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Autocomplete
          size="small"
          options={choices}
          value={selected}
          onChange={(_event, next) => onPartnerChange(next?.id ?? '')}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="No partners match"
          sx={{ minWidth: 260 }}
          renderInput={(params) => <TextField {...params} required={!allowAllPartners} size="small" label="Partner" />}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Month"
            views={['year', 'month']}
            openTo="month"
            format="MMMM YYYY"
            value={dayjs(month, MONTH_FORMAT)}
            onChange={handleMonthChange}
            maxDate={dayjs(currentMonth(), MONTH_FORMAT)}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
          />
        </LocalizationProvider>

        {actions && (
          <Stack direction="row" spacing={1} sx={{ ml: { sm: 'auto' } }}>
            {actions}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
